import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './LoginPage.css';

import { useAdminModeStore } from '../Stores/EditAdminStore';
import { ComponentProps } from './LoginPage';
import { useAuthFlow } from '../../contexts/AuthFlowContext';

// Import new API-based auth service
import {
  getSSOProviderForEmail,
  loginWithEmailPassword,
  loginWithGoogle,
  loginWithSSO,
  handleSSORedirectResult,
  sendResetEmail,
} from '../../services/authService';

const LoginForm: React.FC<ComponentProps> = ({ setActiveComponent: _setActiveComponent }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Get auth flow state from context
  const { message, isRedirecting, setMessage, setIsRedirecting } = useAuthFlow();

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  // On mount: check for SSO redirect errors (user returning from failed SSO)
  useEffect(() => {
    handleSSORedirectResult().then((error) => {
      if (error) {
        setMessage({ type: 'error', text: error });
      }
    });
  }, []);

  // Check if current email requires SSO
  const ssoProvider = getSSOProviderForEmail(email);

  // Note: AuthBootstrap is the single writer for user data and session state
  // The login handlers only initiate Firebase sign-in and show UI messages

  // Email/password login handler
  const handleLogin = async () => {
    setMessage(null);
    setIsLoading(true);

    const result = await loginWithEmailPassword(email, password);

    if (result.requiresSSO) {
      setIsLoading(false);
      setMessage({ type: 'error', text: result.error || 'SSO required' });
      return;
    }

    if (!result.success) {
      setIsLoading(false);
      setMessage({ type: 'error', text: result.error || 'Failed to login' });
      return;
    }

    // On success, Firebase auth state will change and AuthBootstrap will
    // verify the token with the backend, update session, and redirect.
    setIsLoading(false);
  };

  // Google login handler
  const handleGoogleLogin = async () => {
    setMessage(null);
    setIsLoading(true);

    const result = await loginWithGoogle();

    if (!result.success && result.error) {
      setIsLoading(false);
      setMessage({ type: 'error', text: result.error });
      return;
    }

    // If success, AuthBootstrap will handle the rest via onAuthStateChanged
    setIsLoading(false);
  };

  // SSO login handler
  const handleSSOLogin = () => {
    if (ssoProvider) {
      setIsLoading(true);
      setMessage({ type: 'info', text: 'Redirecting to SSO login...' });
      loginWithSSO(ssoProvider);
    }
  };

  // Forgot password handler
  const handleForgotPassword = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!email) {
      setMessage({ type: 'error', text: 'Please enter your email address first.' });
      return;
    }
    setIsLoading(true);
    const result = await sendResetEmail(email);
    setIsLoading(false);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Password reset email sent! Check your inbox.' });
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to send reset email.' });
    }
  };
  return (
    <div className="w-full p-5">
      <div className="h-100 w-full align-items-center">
        <div className="">
          <div className="auth-form-wrapper">
            <p className="text-xl text-red">Log In</p>
            <p className="text-sm text-gray-500 mt-1 mb-3">
              Access is by invitation only. Contact your administrator if you need an account.
            </p>

            {/* Message Display */}
            {message && (
              <div
                className={`p-3 mb-3 rounded ${
                  message.type === 'error'
                    ? 'bg-red-50 border border-red-400 text-red-700'
                    : message.type === 'success'
                    ? 'bg-green-50 border border-green-400 text-green-700'
                    : 'bg-blue-50 border border-blue-200 text-blue-800'
                }`}
                style={{ 
                  fontSize: '14px',
                  animation: message.type === 'error' || message.type === 'success' 
                    ? 'subtle-bounce 0.6s ease-in-out 2' 
                    : 'none'
                }}
              >
                {message.type === 'error' && '⚠️ '}
                {message.type === 'success' && '✓ '}
                {message.text}
              </div>
            )}

            <div className="py-5">
              {/* Email input first - for domain detection */}
              <div className="pb-3">
                <input
                  className="form-control"
                  type="email"
                  placeholder="Work email"
                  value={email}
                  onChange={handleEmailChange}
                  onKeyUp={(e) => (e.key === 'Enter' && password ? handleLogin() : null)}
                  disabled={isLoading || isRedirecting}
                  autoFocus
                />
              </div>

              {/* Dynamic SSO Button - shown when email domain matches */}
              {ssoProvider && (
                <div className="mb-3">
                  <button
                    className="btn btn-primary w-100"
                    style={ssoProvider.buttonStyle ? {
                      backgroundColor: ssoProvider.buttonStyle.bg,
                      color: ssoProvider.buttonStyle.text,
                      borderColor: ssoProvider.buttonStyle.bg
                    } : undefined}
                    onClick={handleSSOLogin}
                    disabled={isLoading || isRedirecting}
                  >
                    🏢 Sign in with {ssoProvider.name}
                  </button>
                  <small className="text-muted d-block mt-1 text-center">
                    Your organization <strong>{ssoProvider.orgName}</strong> uses SSO for authentication
                  </small>
                </div>
              )}

              {/* Password & Google - hidden when SSO is required */}
              {!ssoProvider && (
                <>
                  <div className="pb-3">
                    <input
                      className="form-control"
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={handlePasswordChange}
                      onKeyUp={(e) => (e.key === 'Enter' ? handleLogin() : null)}
                      disabled={isLoading || isRedirecting}
                    />
                  </div>

                  <div className="text-right" style={{ marginTop: '-18px', marginBottom: '16px' }}>
                    <a 
                      href="#" 
                      className={`text-muted text-xs ${(isLoading || isRedirecting) ? 'pointer-events-none opacity-50' : ''}`}
                      onClick={handleForgotPassword}
                    >
                      Forgot password?
                    </a>
                  </div>

                  <div className="mb-3">
                    <button
                      className="btn btn-outline-primary w-100"
                      onClick={handleLogin}
                      disabled={isLoading || isRedirecting}
                    >
                      {isLoading ? 'Logging in...' : isRedirecting ? 'Redirecting...' : 'Log in'}
                    </button>
                  </div>

                  <div className="d-flex align-items-center my-3">
                    <div className="flex-grow-1 border-top"></div>
                    <span className="mx-2 text-muted text-sm">or</span>
                    <div className="flex-grow-1 border-top"></div>
                  </div>

                  <div className="mb-3">
                    <button
                      className="btn btn-secondary btn-logo btn-google w-100"
                      onClick={handleGoogleLogin}
                      disabled={isLoading || isRedirecting}
                    >
                      {isLoading ? 'Signing in...' : isRedirecting ? 'Redirecting...' : 'Continue with Google'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
